import { colors } from "@/src/utils/colors"
import { Framework, FRAMEWORKS } from "@/src/utils/frameworks"
import { createInfoBox, createWarningBox, logLines } from "@/src/utils/cli-ui"
import { logger } from "@/src/utils/logger"
import { existsSync } from "fs"
import path from "path"
import type { Config } from "@/src/utils/get-config"

/**
 * Get the appropriate environment variable prefix for the framework
 */
function getFrameworkEnvPrefix(framework: Framework): string {
  const frameworkName = framework.name

  if (
    frameworkName === FRAMEWORKS["next-app"].name ||
    frameworkName === FRAMEWORKS["next-pages"].name
  ) {
    return "NEXT_PUBLIC_"
  } else if (frameworkName === FRAMEWORKS.astro.name) {
    return "PUBLIC_"
  } else if (
    frameworkName === FRAMEWORKS.vite.name ||
    frameworkName === FRAMEWORKS["tanstack-start"].name ||
    frameworkName === FRAMEWORKS["react-router"].name
  ) {
    return "VITE_"
  } else if (
    frameworkName === FRAMEWORKS.laravel.name ||
    frameworkName === FRAMEWORKS.manual.name
  ) {
    return ""
  } else {
    return ""
  }
}

/**
 * Detect the actual file extension of style files in the project
 */
function detectStyleFileExtension(config?: Config): string {
  if (!config?.resolvedPaths?.styles) {
    return "scss" // Default to scss if no config
  }

  const stylesDir = config.resolvedPaths.styles
  const variablesScss = path.join(stylesDir, "_variables.scss")
  const variablesCss = path.join(stylesDir, "_variables.css")

  // Check if CSS version exists (from Bun conversion)
  if (existsSync(variablesCss)) {
    return "css"
  }

  // Check if SCSS version exists
  if (existsSync(variablesScss)) {
    return "scss"
  }

  // Default to scss for new installations
  return "scss"
}

/**
 * Calculate the correct import path from main CSS file to styles directory
 */
function calculateStylesImportPath(
  framework?: Framework,
  config?: Config
): { cssFile: string; importPath: string } {
  const frameworkName = framework?.name || "unknown"

  // Check if styles are in src directory
  const stylesInSrc = config?.resolvedPaths?.styles?.includes("/src/")
  const hasSrcDir = config?.resolvedPaths?.components?.includes("/src/")

  // Determine the main CSS file location based on framework
  let cssFile = "your main CSS file"
  let importPath = "./styles/"

  switch (frameworkName) {
    case FRAMEWORKS["next-app"].name:
      // Next.js App Router
      if (hasSrcDir) {
        // With srcDir: src/app/globals.css -> ../styles/
        cssFile = "src/app/globals.css"
        importPath = "../styles/"
      } else if (stylesInSrc) {
        // Without srcDir but styles in src: app/globals.css -> ../src/styles/
        cssFile = "app/globals.css"
        importPath = "../src/styles/"
      } else {
        // Without srcDir and styles at root: app/globals.css -> ../styles/
        cssFile = "app/globals.css"
        importPath = "../styles/"
      }
      break

    case FRAMEWORKS["next-pages"].name:
      // Next.js Pages Router
      if (hasSrcDir) {
        // With srcDir: src/pages/_app.tsx imports from ../styles/globals.css
        cssFile = "src/styles/globals.css"
        importPath = "./"
      } else if (stylesInSrc) {
        // Without srcDir but styles in src: pages/_app.tsx imports from ../src/styles/
        cssFile = "src/styles/globals.css"
        importPath = "./"
      } else {
        // Without srcDir: styles/globals.css
        cssFile = "styles/globals.css"
        importPath = "./"
      }
      break

    case FRAMEWORKS.vite.name:
      // Vite - typically src/index.css or src/main.css
      cssFile = "src/index.css" // or src/main.css
      importPath = "./styles/"
      break

    case FRAMEWORKS["react-router"].name:
      // React Router - similar to Vite, uses src structure
      cssFile = "src/index.css" // or src/main.css
      importPath = "./styles/"
      break

    case FRAMEWORKS["tanstack-start"].name:
      // TanStack Start - Vite-based, uses src structure
      cssFile = "src/index.css" // or src/main.css
      importPath = "./styles/"
      break

    case FRAMEWORKS.astro.name:
      // Astro - typically has styles imported in layout files
      if (stylesInSrc) {
        cssFile = "src/layouts/Layout.astro" // or src/pages/index.astro
        importPath = "../styles/"
      } else {
        cssFile = "src/layouts/Layout.astro"
        importPath = "../../styles/" // If styles at root
      }
      break

    case FRAMEWORKS.laravel.name:
      // Laravel - uses resources/css/app.css
      cssFile = "resources/css/app.css"
      // Laravel styles would be in resources/styles/
      importPath = "../styles/"
      break

    case FRAMEWORKS.manual.name:
    default:
      // Manual/Unknown - make best guess based on config
      if (hasSrcDir || stylesInSrc) {
        cssFile = "src/index.css"
        importPath = "./styles/"
      } else {
        cssFile = "your main CSS file"
        importPath = "./styles/"
      }
      break
  }

  return { cssFile, importPath }
}

/**
 * Show styles setup message to remind users to configure styles
 */
export function showStylesConfigurationMessage(
  framework?: Framework,
  config?: Config
) {
  const fileExtension = detectStyleFileExtension(config)
  const { cssFile, importPath } = calculateStylesImportPath(framework, config)

  const content = [
    "The editor requires these style imports:",
    "",
    `Add to ${colors.cyan(cssFile)}:`,
    "",
    colors.yellow(`  @import '${importPath}_variables.${fileExtension}';`),
    colors.yellow(
      `  @import '${importPath}_keyframe-animations.${fileExtension}';`
    ),
    "",
    "Without these imports, the editor will not display correctly.",
    "",
    `Setup guide: ${colors.cyan("https://tiptap.dev/docs/ui-components/getting-started/style")}`,
  ]

  const box = createWarningBox("Action Required: Import Styles", content)
  logLines(box)
}

/**
 * Show package manager consistency warning
 */
export function showPackageManagerWarning(projectPackageManager: string) {
  const content = [
    `Your project uses ${colors.cyan(projectPackageManager)} as its package manager.`,
    "",
    "For consistency, use the same package manager for all operations:",
    `• Install dependencies: ${colors.yellow(`${projectPackageManager} install`)}`,
    `• Add packages: ${colors.yellow(`${projectPackageManager} add [package]`)}`,
    `• Run scripts: ${colors.yellow(`${projectPackageManager} run [script]`)}`,
    "",
    "Mixing package managers (npm, pnpm, yarn) can cause conflicts.",
  ]

  const box = createInfoBox("Package Manager Detected", content)
  logLines(box)
}

/**
 * Show environment variables setup for Notion template (without styles)
 */
export function showNotionEnvSetupMessage(framework: Framework) {
  const prefix = getFrameworkEnvPrefix(framework)

  const content = [
    "The Notion-like template requires environment variables for collaborative features:",
    "",
    colors.yellow(`${prefix}TIPTAP_COLLAB_DOC_PREFIX`) +
      " - Document prefix (required)",
    colors.yellow(`${prefix}TIPTAP_COLLAB_APP_ID`) +
      " - Document Server App ID (required)",
    colors.yellow(`${prefix}TIPTAP_COLLAB_TOKEN`) +
      " - JWT token for collaboration (required)",
    colors.yellow(`${prefix}TIPTAP_AI_APP_ID`) + " - AI App ID (required)",
    colors.yellow(`${prefix}TIPTAP_AI_TOKEN`) +
      " - JWT token for AI (required)",
    "",
    "Get these credentials from your account at cloud.tiptap.dev",
    "",
    `Setup guide: ${colors.cyan("https://tiptap.dev/docs/ui-components/templates/notion-like-editor")}`,
  ]

  const box = createWarningBox("Action Required: Environment Setup", content)
  logLines(box)
}

/**
 * Show combined Notion setup message with environment variables
 */
export function showNotionSetupMessage(framework: Framework) {
  const prefix = getFrameworkEnvPrefix(framework)

  const content = [
    "The Notion template requires these environment variables:",
    "",
    colors.yellow(`${prefix}TIPTAP_COLLAB_DOC_PREFIX`) +
      " - Document prefix (required)",
    colors.yellow(`${prefix}TIPTAP_COLLAB_APP_ID`) +
      " - Document Server App ID (required)",
    colors.yellow(`${prefix}TIPTAP_COLLAB_TOKEN`) +
      " - JWT token for collaboration (required)",
    colors.yellow(`${prefix}TIPTAP_AI_APP_ID`) + " - AI App ID (required)",
    colors.yellow(`${prefix}TIPTAP_AI_TOKEN`) +
      " - JWT token for AI (required)",
    "",
    "Get these credentials from your account at cloud.tiptap.dev",
    "",
    `Setup guide: ${colors.cyan("https://tiptap.dev/docs/ui-components/templates/notion-like-editor")}`,
  ]

  const box = createWarningBox("Action Required: Environment Setup", content)
  logLines(box)
}

/**
 * Show combined Notion template setup message
 */
export function showNotionTemplateSetupMessage(
  framework: Framework,
  config?: Config
) {
  const prefix = getFrameworkEnvPrefix(framework)
  const fileExtension = detectStyleFileExtension(config)
  const { cssFile, importPath } = calculateStylesImportPath(framework, config)

  const content = [
    "The Notion-like template is a complete editor application that requires",
    "additional configuration before it can be used:",
    "",
    colors.cyan("1. Import Required Styles"),
    `   Add to ${colors.yellow(cssFile)}:`,
    colors.gray(`   @import '${importPath}_variables.${fileExtension}';`),
    colors.gray(
      `   @import '${importPath}_keyframe-animations.${fileExtension}';`
    ),
    "",
    colors.cyan("2. Add Environment Variables (for collaborative features)"),
    `   Add to your .env file:`,
    colors.gray(`   ${prefix}TIPTAP_COLLAB_DOC_PREFIX - Document prefix`),
    colors.gray(`   ${prefix}TIPTAP_COLLAB_APP_ID - Document Server App ID`),
    colors.gray(
      `   ${prefix}TIPTAP_COLLAB_TOKEN - JWT token for collaboration`
    ),
    colors.gray(`   ${prefix}TIPTAP_AI_APP_ID - AI App ID`),
    colors.gray(`   ${prefix}TIPTAP_AI_TOKEN - JWT token for AI`),
    "",
    `Get credentials at ${colors.cyan("https://cloud.tiptap.dev")}`,
    `Setup guide: ${colors.cyan("https://tiptap.dev/docs/ui-components/templates/notion-like-editor")}`,
  ]

  const box = createWarningBox("Next Steps: Configure your Editor", content)
  logLines(box)
}

/**
 * Show Table Node integration guide
 */
export function showTableNodeSetupMessage() {
  logger.log("")
  logger.log(colors.cyan("→ Table Node integration guide:"))
  logger.log(
    `  ${colors.dim("https://tiptap.dev/docs/ui-components/node-components/table-node#usage")}`
  )
  logger.log("")
}
